import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { booleanAttribute, Directive, Input, numberAttribute, signal, Signal, TemplateRef } from '@angular/core';
import { ColumnMenuSettings, FilterableSettings } from '@progress/kendo-angular-grid';
import { Subject } from 'rxjs';

@Directive({
    selector: 'nova-grid-column',
    standalone: true
})
export class GridColumnDirective {
    //it is defined if we have multiple columns sharing same field
    @Input() uniqueName = '';
    @Input() field = '';
    @Input() title = '';
    @Input() format: 'text' | 'date' | 'currency' | 'boolean' = 'text';
    @Input() class = '';
    @Input() headerStyle: any;
    @Input() template?: TemplateRef<any>;
    @Input() filterable: FilterableSettings = 'row';
    @Input() columnMenu: boolean | ColumnMenuSettings = false;
    @Input() filterType:
        | 'checkbox'
        | 'multiselect'
        | 'autocomplete-multiselect'
        | 'autocomplete-multiselect-checkbox'
        | 'text'
        | 'number'
        | 'date'
        | 'dropdown'
        | 'daterange'
        | 'numberrange'
        | 'none' = 'none';

    @Input() filterOptions: any;
    @Input() filterTemplate?: TemplateRef<any>;
    @Input() filterCellTemplate?: TemplateRef<any>;
    @Input() groupTemplate?: TemplateRef<any>;
    @Input() headerTemplate?: TemplateRef<any>;
    @Input() footerTemplate?: TemplateRef<any>;
    @Input({ transform: booleanAttribute }) sortable = false;
    @Input() sortField: string | null = null;
    @Input() textField = '';
    @Input() valueField = '';
    @Input() defaultItemText = '';
    @Input() columnExported?: (data: any, dataItem: any) => string | void;
    @Input() filterByCustomValue = false;
    @Input() showIcon = false;
    @Input({ transform: numberAttribute }) maxFilterOptionRows = 50;
    @Input() searchText: Subject<string> = new Subject<string>();
    @Input({ transform: booleanAttribute }) selectAllFilterOptions = false;
    @Input({ transform: numberAttribute }) minFilterInputLength = 4;
    @Input() applyFilterOnChange = true;
    @Input() filterStyle: any;
    @Input() persistLookup = false;
    @Input() style: any;
    @Input() orderIndex = 0;
    @Input() uniqueFilterClass = '';
    @Input({ transform: booleanAttribute }) selected = false;
    @Input({ transform: booleanAttribute }) needSameDaySelection = false;

    private readonly _hiddenSig = signal(false);
    get hiddenSig(): Signal<boolean> {
        return this._hiddenSig;
    }

    get hidden() {
        return this._hiddenSig();
    }

    @Input({ transform: booleanAttribute })
    set hidden(v: boolean) {
        this._hiddenSig.set(coerceBooleanProperty(v));
    }

    // Default to Infinity to mimic "auto" width
    // This also matches the jQuery version of Kendo
    private _width = Infinity;
    private _popupWidth = 0;

    get width(): number {
        return this._width;
    }

    get popupWidth(): string {
        return this._popupWidth.toString();
    }

    @Input() set width(colWidth: number | string) {
        if (typeof colWidth === 'string') {
            colWidth = colWidth.includes('px') ? colWidth.substring(0, colWidth.length - 2) : colWidth;
        }

        this._width = coerceNumberProperty(colWidth);
    }

    @Input() set popupWidth(width: number | string) {
        this._popupWidth = coerceNumberProperty(width);
    }

    private _minResizableWidth = 10; // default Kendo grid value
    get minResizableWidth(): number {
        return this._minResizableWidth;
    }
    @Input() set minResizableWidth(width: number | string) {
        this._minResizableWidth = coerceNumberProperty(width);
    }
}
